import { format } from 'date-fns';
import * as React from 'react';
import { create } from 'zustand';

import { EditIcon } from '../../../assets/icons/general/edit-4';
import { cn } from '../../../lib/utils';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

import {
  editableDateFieldVariants,
  editableDateFieldIconVariants,
} from './variants';

export interface EditableDateFieldProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  defaultEditable?: boolean;
  /**
   * When true, clicking the field immediately opens the calendar popover.
   * Defaults to false to preserve existing "enter edit mode first" behavior.
   */
  openOnEdit?: boolean;
  /**
   * When true, the field sizes to fit its content instead of stretching to full width.
   * Defaults to false to preserve existing full-width behavior.
   */
  fitContent?: boolean;
  dateFormat?: string;
  calendarProps?: Partial<React.ComponentProps<typeof Calendar>>;
}

const createEditableDateFieldStore = ({
  initialValue,
  defaultEditable,
}: {
  initialValue?: Date;
  defaultEditable: boolean;
}) =>
  create<EditableDateFieldState>((set) => ({
    isEditing: defaultEditable,
    isCalendarOpen: false,
    internalValue: initialValue,
    setIsEditing: (isEditing) => set({ isEditing }),
    setIsCalendarOpen: (isCalendarOpen) => set({ isCalendarOpen }),
    setInternalValue: (internalValue) => set({ internalValue }),
  }));

interface EditableDateFieldState {
  isEditing: boolean;
  isCalendarOpen: boolean;
  internalValue?: Date;
  setIsEditing: (isEditing: boolean) => void;
  setIsCalendarOpen: (isOpen: boolean) => void;
  setInternalValue: (date?: Date) => void;
}

const EditableDateField = React.forwardRef<
  HTMLDivElement,
  EditableDateFieldProps
>(
  (
    {
      value,
      onChange,
      onEditStart,
      onEditEnd,
      placeholder = 'Select date',
      disabled = false,
      className,
      defaultEditable = false,
      openOnEdit = false,
      fitContent = false,
      dateFormat = 'PPP', // e.g., "Nov 21, 2025"
      calendarProps,
    },
    ref,
  ) => {
    // Store is created once per component instance and synced via useEffect
    const dateFieldStoreRef = React.useRef<ReturnType<
      typeof createEditableDateFieldStore
    > | null>(null);
    if (!dateFieldStoreRef.current) {
      dateFieldStoreRef.current = createEditableDateFieldStore({
        initialValue: value,
        defaultEditable,
      });
    }
    const dateFieldStore = dateFieldStoreRef.current;

    const isEditing = dateFieldStore((state) => state.isEditing);
    const isCalendarOpen = dateFieldStore((state) => state.isCalendarOpen);
    const internalValue = dateFieldStore((state) => state.internalValue);
    const setIsEditing = dateFieldStore((state) => state.setIsEditing);
    const setIsCalendarOpen = dateFieldStore(
      (state) => state.setIsCalendarOpen,
    );
    const setInternalValue = dateFieldStore((state) => state.setInternalValue);

    React.useEffect(() => {
      setInternalValue(value);
    }, [value, setInternalValue]);

    React.useEffect(() => {
      setIsEditing(defaultEditable);
    }, [defaultEditable, setIsEditing]);

    const handleEditClick = () => {
      if (disabled) return;
      setIsEditing(true);
      if (openOnEdit) {
        setIsCalendarOpen(true);
      }
      onEditStart?.();
    };

    const handleDateSelect = (date: Date | undefined) => {
      setInternalValue(date);
      onChange?.(date);
      // Close after selection
      setTimeout(() => {
        setIsCalendarOpen(false);
        setIsEditing(false);
        onEditEnd?.();
      }, 150);
    };

    const handleOpenChange = (open: boolean) => {
      setIsCalendarOpen(open);
      if (!open && isEditing) {
        setIsEditing(false);
        onEditEnd?.();
      }
    };

    const displayValue = internalValue
      ? format(internalValue, dateFormat)
      : placeholder;

    if (isEditing) {
      return (
        <Popover open={isCalendarOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <div
              ref={ref}
              contentEditable={false}
              className={cn(
                editableDateFieldVariants({ mode: 'edit', disabled }),
                fitContent && 'w-fit inline-flex',
                className,
              )}
              role="button"
              tabIndex={disabled ? -1 : 0}
            >
              <span
                className={cn(
                  fitContent ? 'whitespace-nowrap' : 'flex-1 truncate',
                  !internalValue && 'text-muted-foreground',
                )}
              >
                {displayValue}
              </span>
              <EditIcon
                className={cn(
                  'h-4 w-4',
                  editableDateFieldIconVariants({ mode: 'edit', disabled }),
                )}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-3 max-w-[17.5rem]"
            align="start"
            sideOffset={4}
          >
            <Calendar
              mode="single"
              selected={internalValue as any}
              onSelect={handleDateSelect as any}
              initialFocus
              disabled={disabled}
              {...calendarProps}
            />
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <div
        ref={ref}
        contentEditable={false}
        onMouseDown={(e) => {
          // Stop propagation to prevent BlockNote from handling mouse events
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Don't stop propagation on click - let it work naturally
          // This allows the click to properly trigger edit mode
          handleEditClick();
        }}
        className={cn(
          editableDateFieldVariants({ mode: 'view', disabled }),
          fitContent && 'w-fit inline-flex',
          className,
        )}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleEditClick();
          }
        }}
      >
        <span
          className={cn(
            fitContent ? 'whitespace-nowrap' : 'flex-1 truncate',
            !internalValue && 'text-muted-foreground',
          )}
        >
          {displayValue}
        </span>
        <EditIcon
          className={cn(
            'h-4 w-4',
            editableDateFieldIconVariants({ mode: 'view', disabled }),
          )}
        />
      </div>
    );
  },
);

EditableDateField.displayName = 'EditableDateField';

export { EditableDateField };
